/**
 * CanWeHelp.js — "Can We Help?" contact/enquiry modal.
 *
 * Allows visitors to submit a quick enquiry form with name, email, phone,
 * and a message. Posts to the engine API and shows a success or error alert.
 * Triggered by the floating help button in the Footer.
 *
 * Props:
 *  - CanWeHelpStatus {boolean}         Whether the modal is visible.
 *  - setCanWeHelpStatus {Function}     Opens/closes the modal.
 *  - EngineURL {string}                Base URL for the engine API.
 */

// ─── External libraries ───────────────────────────────────────────────────────
import React, {useState, useEffect} from "react";
import {
  // Card,
  Button,
  Modal,
  Alert,
  Form,
  Col,
} from "react-bootstrap";
import axios from "axios";
import "react-phone-number-input/style.css";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const CanWeHelp = (props) => {    

  const [userEmail, setUserEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [newMobile, setnewMobile] = useState("");
  const [Inquiry, setInquiry] = useState("");
  const [SuccessMessage, setSuccessMessage] = useState(undefined);
  const [ErrorMessage, setErrorMessage] = useState(undefined);
  const [SomethingUpadating, setSomethingUpadating] = useState(false);
  //console.log(privacyPolicy);
  const [userEmail_ER, setUserEmail_ER] = useState(false);
  const [firstName_ER, setFirstName_ER] = useState(false);
  const [Mobile_ER, setMobile_ER] = useState(false);

  /**
   * Reset the whole form (fields, validation errors, and status messages) each
   * time the modal closes, so reopening always shows a clean form instead of the
   * previous values / error messages.
   */
  useEffect(() => {
    if (!props.CanWeHelpStatus) {
      setUserEmail("");
      setFirstName("");
      setnewMobile("");
      setInquiry("");
      setSuccessMessage(undefined);
      setErrorMessage(undefined);
      setSomethingUpadating(false);
      setUserEmail_ER(false);
      setFirstName_ER(false);
      setMobile_ER(false);
    }
  }, [props.CanWeHelpStatus]);

    const handleSubmit = (event) => {
        event.preventDefault();
        setSuccessMessage(undefined);
        setErrorMessage(undefined);

        // ── Validate synchronously into local vars (React batching fix) ────────
        const err_name   = !firstName.trim();
        const err_email  = !userEmail.trim() || !EMAIL_RE.test(userEmail.trim());
        // newMobile can be undefined if user never touched the field — guard it
        const err_mobile = !newMobile || !isValidPhoneNumber(String(newMobile));

        setFirstName_ER(err_name);
        setUserEmail_ER(err_email);
        setMobile_ER(err_mobile);

        if (err_name || err_email || err_mobile) return;

        // ── All valid — submit ─────────────────────────────────────────────────
        setSomethingUpadating(true);

        const inquirData = {
            name:    firstName.trim(),
            email:   userEmail.trim(),
            phone:   newMobile,
            message: Inquiry.trim(),
        };

        axios
            .post(props.EngineURL + "canwehelp/index.php", inquirData)
            .then(function (response) {
                // Removed setTimeout — no benefit, causes stale state on slow connections
                if (response?.data?.code === 200) {
                    setSuccessMessage(response.data.message);
                    setErrorMessage(undefined);
                } else {
                    setErrorMessage(response?.data?.message || "Something went wrong. Please try again.");
                    setSuccessMessage(undefined);
                }
                setSomethingUpadating(false);
            })
            .catch(function (error) {
                // Never console.log in production — show user-facing message instead
                setSomethingUpadating(false);
                setSuccessMessage(undefined);

                if (error.response) {
                    if (error.response.status === 429) {
                        setErrorMessage("Too many requests. Please wait a moment and try again.");
                    } else if (error.response.status >= 500) {
                        setErrorMessage("A server error occurred. Please try again later.");
                    } else {
                        setErrorMessage("Something went wrong. Please try again.");
                    }
                } else if (error.request) {
                    setErrorMessage("Network error. Please check your connection and try again.");
                } else {
                    setErrorMessage("Something went wrong. Please try again.");
                }
            });
    };

    return (
        <>
            <Modal
                className="signup_model"
                show={props.CanWeHelpStatus}
                onHide={() => props.setCanWeHelpStatus(false)}
            >
                <Modal.Header closeButton className="border-0 pb-0" />
                <Modal.Body className="pt-0 px-4">
                    {SuccessMessage !== undefined ? (
                        <h5 className="text-center">
                            We received your enquiry. We'll get back to you as soon as practical.
                        </h5>
                    ) : (
                        <Modal.Title className="text-center">How can we help you?</Modal.Title>
                    )}

                    <div className="about_page_wrapper">
                        {SuccessMessage === undefined && (
                            <div className="wFormContainer">
                                <Form onSubmit={handleSubmit}>

                                    <Form.Group as={Col} md="12" xs="12" className="form-group">
                                        <Form.Label htmlFor="canwehelpname">Your name *</Form.Label>
                                        <Form.Control
                                            className={firstName_ER ? "form-error" : ""}
                                            type="text"
                                            id="canwehelpname"
                                            placeholder="Your full name"
                                            value={firstName}
                                            disabled={SomethingUpadating}
                                            onChange={(e) => { setFirstName(e.target.value); if (firstName_ER) setFirstName_ER(false); }}
                                        />
                                        {firstName_ER &&
                                            <span className="field-error-msg">Your name is required</span>
                                        }
                                    </Form.Group>

                                    <Form.Group as={Col} md="12" className="form-group">
                                        <Form.Label htmlFor="canwehelpemail">Your Email *</Form.Label>
                                        <Form.Control
                                            type="email"
                                            id="canwehelpemail"
                                            className={userEmail_ER ? "form-error" : ""}
                                            placeholder="Your email address"
                                            value={userEmail}
                                            disabled={SomethingUpadating}
                                            onChange={(e) => { setUserEmail(e.target.value); if (userEmail_ER) setUserEmail_ER(false); }}
                                        />
                                        {userEmail_ER &&
                                            <span className="field-error-msg">Please enter a valid email address</span>
                                        }
                                    </Form.Group>

                                    <Form.Group as={Col} md="12" className="form-group">
                                        <Form.Label htmlFor="canwehelpmobile">Your Mobile *</Form.Label>
                                        <PhoneInput
                                            international
                                            className={Mobile_ER ? "form-error PhoneInput" : "PhoneInput"}
                                            defaultCountry="AU"
                                            placeholder="Your mobile number"
                                            value={newMobile}
                                            disabled={SomethingUpadating}
                                            onChange={(val) => { setnewMobile(val); if (Mobile_ER) setMobile_ER(false); }}
                                        />
                                        {Mobile_ER &&
                                            <span className="field-error-msg">Please enter a valid mobile number</span>
                                        }
                                    </Form.Group>

                                    <Form.Group as={Col} md="12" className="form-group">
                                        <Form.Label htmlFor="canwehelpinquiry">Your Inquiry</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            id="canwehelpinquiry"
                                            placeholder="Your inquiry"
                                            value={Inquiry}
                                            disabled={SomethingUpadating}
                                            onChange={(e) => setInquiry(e.target.value)}
                                        />
                                    </Form.Group>

                                    {ErrorMessage !== undefined &&
                                        <Alert variant="danger">{ErrorMessage}</Alert>
                                    }
                                    {SomethingUpadating &&
                                        <Alert variant="primary">Sending…</Alert>
                                    }

                                    <Form.Group as={Col} xs="12" className="form-group text-center">
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            className="btn-lg border-2 rounded-2"
                                            disabled={SomethingUpadating}
                                        >
                                            {SomethingUpadating ? "Sending…" : "Submit"}
                                        </Button>
                                    </Form.Group>

                                </Form>
                            </div>
                        )}
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};
